// DocNavigator — prev/next and breadcrumb traversal over the flat, ordered
// `NavItem[]` array (RFC-0010 §6, PD-0008 G19).
//
// The nav array is RFC-0006's flat contract: rows ordered by `position` ASC,
// each carrying a `parentId` (its parent row's `position`, or null at the root)
// and a `docPath` (non-null for a leaf doc, null for a pure section node). No
// `children` tree is sent — the client reconstructs relationships from these
// fields. These helpers are pure so they unit-test without a DOM.

import type { NavItem } from '@portal/shared-types';

export interface PrevNext {
  prev: NavItem | null;
  next: NavItem | null;
}

/** A `NavItem` with its children reconstructed from the flat array. */
export interface NavTreeNode extends NavItem {
  children: NavTreeNode[];
}

/**
 * Reconstruct the nav tree from the flat, ordered array. Each row's `parentId`
 * references a parent row's `position`; rows with a null (or dangling) parent
 * become roots. Order is preserved by processing the `position`-sorted array.
 */
export function buildNavTree(nav: NavItem[]): NavTreeNode[] {
  const sorted = [...nav].sort((a, b) => a.position - b.position);
  const byPosition = new Map<number, NavTreeNode>();
  for (const item of sorted) byPosition.set(item.position, { ...item, children: [] });

  const roots: NavTreeNode[] = [];
  for (const item of sorted) {
    const node = byPosition.get(item.position)!;
    const parent =
      item.parentId !== null && item.parentId !== undefined
        ? byPosition.get(item.parentId)
        : undefined;
    if (parent && parent !== node) parent.children.push(node);
    else roots.push(node);
  }
  return roots;
}

/** A breadcrumb segment; `docPath` is null for the repo root and section nodes. */
export interface Breadcrumb {
  label: string;
  docPath: string | null;
}

/** Nav sorted by `position` ASC (defensive — the API already orders it so). */
function ordered(nav: NavItem[]): NavItem[] {
  return [...nav].sort((a, b) => a.position - b.position);
}

/**
 * The previous/next *leaf* docs around `currentDocPath`. Section nodes (null
 * `docPath`) are skipped: prev/next index over the ordered leaf subset only, so
 * navigation never lands on a non-doc heading.
 */
export function getPrevNext(nav: NavItem[], currentDocPath: string): PrevNext {
  const leaves = ordered(nav).filter((n) => n.docPath !== null);
  const idx = leaves.findIndex((n) => n.docPath === currentDocPath);
  if (idx === -1) return { prev: null, next: null };
  return {
    prev: idx > 0 ? leaves[idx - 1] : null,
    next: idx < leaves.length - 1 ? leaves[idx + 1] : null,
  };
}

/**
 * Breadcrumbs for the doc at `currentDocPath`: `[repo name, ...ancestor section
 * titles, doc title]`. Walks the `parentId` → parent `position` ancestry up to
 * the root, then reverses. Returns just `[repo name]` when the path is unknown.
 */
export function getBreadcrumbs(
  nav: NavItem[],
  currentDocPath: string,
  repoName: string,
): Breadcrumb[] {
  const root: Breadcrumb = { label: repoName, docPath: null };

  const byPosition = new Map<number, NavItem>();
  for (const item of nav) byPosition.set(item.position, item);

  const current = nav.find((n) => n.docPath === currentDocPath);
  if (!current) return [root];

  // Walk up the parentId chain, collecting nodes leaf → root.
  const chain: NavItem[] = [current];
  let parentId = current.parentId;
  const seen = new Set<number>([current.position]);
  while (parentId !== null && parentId !== undefined) {
    const parent = byPosition.get(parentId);
    // Guard against a malformed self/cyclic parent reference.
    if (!parent || seen.has(parent.position)) break;
    chain.push(parent);
    seen.add(parent.position);
    parentId = parent.parentId;
  }

  chain.reverse();
  return [root, ...chain.map((n) => ({ label: n.title, docPath: n.docPath }))];
}
